import type { SessionStore } from '@hostesa/auth-client';

/**
 * Consumes an operator impersonation token handed off via the URL fragment.
 *
 * The back-office hands the operator into the platform app by appending
 * `#imp=<url-encoded-token>` to the platform URL. We read that token out of the
 * fragment, store it in the (in-memory) session store, and immediately strip the
 * fragment from the address bar so the token never lingers in history, is never
 * copy-pasteable, and never leaks via the Referer header on subsequent requests.
 *
 * Returns the consumed token when an `#imp=` fragment was present, or `null`
 * when there was nothing to handle. The caller uses the returned token to also
 * seed the synchronous auth snapshot (see `main.tsx`) so the very first
 * `sessionGate` already sees a logged-in session — the session store alone is
 * not enough because the router guard reads the snapshot, which `AuthBridge`
 * only publishes after the first paint.
 *
 * SECURITY:
 * - The fragment (`#…`) is never sent to the server, so the token is not exposed
 *   in server access logs during the handoff navigation.
 * - We replace (not push) the history entry so the back button cannot resurface
 *   the token-bearing URL.
 * - The session store is in-memory; a full reload drops the token and ends the
 *   impersonation session — acceptable per the impersonation design.
 */
export function consumeImpersonationHandoff(sessionStore: SessionStore): string | null {
  const hash = window.location.hash;
  const match = /^#imp=(.+)$/.exec(hash);
  if (!match?.[1]) return null;

  const token = decodeURIComponent(match[1]);
  sessionStore.setToken(token);

  // Strip the fragment so the token does not linger in the URL/history.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  return token;
}
